import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';

import { useNow } from '@gitbook/util-react-hooks';

const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;
const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Atom storing the timestamps of the messages sent to the agent over the last 30 days.
 */
const agentUsageTimestampsAtom = atomWithStorage<number[]>('agentUsageTimestamps', [], undefined, {
    getOnInit: true,
});

/**
 * Record a user message sent to the agent.
 */
export function useRecordAgentUsage(): () => void {
    const setTimestamps = useSetAtom(agentUsageTimestampsAtom);

    return React.useCallback(() => {
        setTimestamps((timestamps) => {
            const now = Date.now();
            const recentTimestamps = filterRecentTimestamps(timestamps, now, THIRTY_DAYS_IN_MS);
            return [...recentTimestamps, now];
        });
    }, [setTimestamps]);
}

/**
 * Get how many messages the user sent to the agent over the last 30 days and the last 7 days.
 */
export function useAgentUsage() {
    const timestamps = useAtomValue(agentUsageTimestampsAtom);
    const now = useNow(60 * 60 * 1000);

    return React.useMemo(() => {
        const recent30Days = filterRecentTimestamps(timestamps, now, THIRTY_DAYS_IN_MS);
        const recent7Days = filterRecentTimestamps(timestamps, now, SEVEN_DAYS_IN_MS);
        return {
            total: recent30Days.length,
            weeklyTotal: recent7Days.length,
        };
    }, [timestamps, now]);
}

function filterRecentTimestamps(timestamps: number[], now: number, windowMs: number) {
    const cutoff = now - windowMs;
    return timestamps.filter((timestamp) => timestamp >= cutoff);
}
